import careConfig from "@careConfig";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { formatPhoneNumberIntl } from "react-phone-number-input";

import PrintPreview from "@/CAREUI/misc/PrintPreview";

import PrintFooter from "@/components/Common/PrintFooter";
import PrintTable from "@/components/Common/PrintTable";
import { getFrequencyDisplay } from "@/components/Medicine/MedicationsTable";
import { formatDosage, formatSig } from "@/components/Medicine/utils";

import { formatDateTime, formatName, formatPatientAge } from "@/Utils/utils";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { EncounterRead } from "@/types/emr/encounter/encounter";
import { displayMedicationName } from "@/types/emr/medicationRequest/medicationRequest";
import { PatientRead } from "@/types/emr/patient/patient";
import { PrescriptionGroup } from "@/types/emr/prescription/prescription";
import { PatientIdentifierUse } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";

interface DetailRowProps {
  label: string;
  value?: string | null;
  isStrong?: boolean;
}

interface PrescriptionContentProps {
  prescription: PrescriptionGroup;
}

const PrescriptionContent = ({ prescription }: PrescriptionContentProps) => {
  const medications = prescription.requests;
  const { t } = useTranslation();

  const medicationsWithProduct = medications.filter(
    (med) => med.requested_product,
  );

  const medicationsWithoutProduct = medications.filter(
    (med) => !med.requested_product,
  );

  return (
    <div>
      {/* Prescription Symbol */}
      <div className="text-2xl font-semibold mb-3 flex items-end gap-4">
        <p>{t("℞")}</p>
        <p className="text-sm text-gray-600 font-semibold ">
          {formatDateTime(
            prescription.prescription?.created_date ||
              medications[0].created_date,
            "DD/MM/YYYY hh:mm A",
          )}
        </p>
      </div>
      {/* Medications Table */}
      {medicationsWithProduct && medicationsWithProduct.length > 0 && (
        <div className="mt-4">
          <p className="text-base font-semibold mb-2">{t("medicines")}</p>
          <PrintTable
            headers={[
              { key: "medicine" },
              { key: "dosage" },
              { key: "frequency" },
              { key: "duration" },
              { key: "instructions" },
            ]}
            rows={medicationsWithProduct?.map((medication) => {
              const instruction = medication.dosage_instruction[0];
              const frequency = getFrequencyDisplay(instruction?.timing);
              const dosage = formatDosage(instruction);
              const duration = instruction?.timing?.repeat?.bounds_duration;
              const remarks = formatSig(instruction);
              const notes = medication.note;
              return {
                medicine: displayMedicationName(medication),
                status: t(`medication_status_${medication.status}`),
                dosage: dosage,
                frequency: instruction?.as_needed_boolean
                  ? `${t("as_needed_prn")} (${instruction?.as_needed_for?.display ?? "-"})`
                  : (frequency?.meaning ?? "-") +
                    (instruction?.additional_instruction?.[0]?.display
                      ? `, ${instruction.additional_instruction[0].display}`
                      : ""),
                duration: duration ? `${duration.value} ${duration.unit}` : "-",
                instructions: `${remarks || "-"}${notes ? ` (${t("note")}: ${notes})` : ""}`,
              };
            })}
          />
        </div>
      )}
      {/* External Medications Table */}
      {medicationsWithoutProduct && medicationsWithoutProduct.length > 0 && (
        <div className="mt-4">
          <p className="text-base font-semibold mb-2">{t("not_in_stock")}</p>
          <PrintTable
            headers={[
              { key: "medicine" },
              { key: "dosage" },
              { key: "frequency" },
              { key: "duration" },
              { key: "instructions" },
            ]}
            rows={medicationsWithoutProduct?.map((medication) => {
              const instruction = medication.dosage_instruction[0];
              const frequency = getFrequencyDisplay(instruction?.timing);
              const dosage = formatDosage(instruction);
              const duration = instruction?.timing?.repeat?.bounds_duration;
              const remarks = formatSig(instruction);
              const notes = medication.note;
              return {
                medicine: displayMedicationName(medication),
                status: t(`medication_status_${medication.status}`),
                dosage: dosage,
                frequency: instruction?.as_needed_boolean
                  ? `${t("as_needed_prn")} (${instruction?.as_needed_for?.display ?? "-"})`
                  : (frequency?.meaning ?? "-") +
                    (instruction?.additional_instruction?.[0]?.display
                      ? `, ${instruction.additional_instruction[0].display}`
                      : ""),
                duration: duration ? `${duration.value} ${duration.unit}` : "-",
                instructions: `${remarks || "-"}${notes ? ` (${t("note")}: ${notes})` : ""}`,
              };
            })}
          />
        </div>
      )}
      {/* Doctor's Signature */}
      <div className="w-full items-end mt-6 flex flex-row justify-end gap-1">
        <div className="text-right">
          <p className="text-sm text-gray-400">{t("prescribed_by")}</p>
          <p className="text-sm text-gray-600 font-semibold">
            {formatName(
              prescription.prescription?.prescribed_by ||
                medications[0].created_by,
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, isStrong = false }: DetailRowProps) => {
  return (
    <div className="flex">
      <span className="text-gray-600 w-32">{label}</span>
      <span className="text-gray-600">: </span>
      <span className={`ml-1 ${isStrong ? "font-semibold" : ""}`}>
        {value || "-"}
      </span>
    </div>
  );
};

interface PrescriptionPreviewProps {
  encounter?: EncounterRead;
  prescriptions: PrescriptionGroup[];
  patient: PatientRead;
}

export const PrescriptionPreview = ({
  encounter,
  prescriptions,
  patient,
}: PrescriptionPreviewProps) => {
  const { t } = useTranslation();
  const { facility } = useCurrentFacility();

  if (!prescriptions?.length) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-lg border-2 border-dashed p-4 text-gray-500 border-gray-200">
        {t("no_medications_found_for_this_encounter")}
      </div>
    );
  }

  return (
    <PrintPreview
      title={`${t("prescriptions")} - ${patient.name}`}
      disabled={!prescriptions?.length}
    >
      <div className="max-w-4xl mx-auto">
        <div>
          {/* Header */}
          <div className="flex justify-between items-start mb-4 pb-2 border-b border-gray-200">
            <div className="flex items-start gap-4">
              <div className="text-left">
                <h1 className="text-2xl font-medium">{facility?.name}</h1>
                {facility?.address && (
                  <div className="text-gray-500 whitespace-pre-wrap wrap-break-word text-sm">
                    {facility.address}
                    {facility.phone_number && (
                      <p className="text-gray-500 text-sm">
                        {t("phone")}: {facility.phone_number}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <img
              src={careConfig.mainLogo?.dark}
              alt="Logo"
              className="h-10 w-auto object-contain mb-2 sm:mb-0 text-end"
            />
          </div>

          {/* Patient Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-6 pb-3">
            <div className="space-y-1">
              <DetailRow label={t("patient")} value={patient.name} isStrong />
              <DetailRow
                label={`${t("age")} / ${t("sex")}`}
                value={
                  patient
                    ? `${formatPatientAge(patient, true)}, ${t(`GENDER__${patient.gender}`)}`
                    : undefined
                }
                isStrong
              />
              {encounter?.patient?.instance_identifiers
                ?.filter(
                  ({ config }) =>
                    config.config.use === PatientIdentifierUse.official,
                )
                .map((identifier) => (
                  <DetailRow
                    key={identifier.config.id}
                    label={identifier.config.config.display}
                    value={identifier.value}
                    isStrong
                  />
                ))}
            </div>
            <div className="space-y-1">
              <DetailRow
                label={t("date")}
                value={
                  encounter?.period?.start
                    ? format(
                        new Date(encounter.period.start),
                        "dd MMM yyyy, EEEE",
                      )
                    : format(new Date(), "dd MMM yyyy, EEEE")
                }
                isStrong
              />
              <DetailRow
                label={t("mobile_number")}
                value={patient && formatPhoneNumberIntl(patient.phone_number)}
                isStrong
              />
            </div>
          </div>

          {prescriptions.map((groupedPrescription) => (
            <PrescriptionContent
              key={groupedPrescription.prescription?.id || "no-prescription"}
              prescription={groupedPrescription}
            />
          ))}

          {/* Footer */}
          <PrintFooter leftContent={t("computer_generated_prescription")} />
        </div>
      </div>
    </PrintPreview>
  );
};
